import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneLight } from 'react-syntax-highlighter/dist/cjs/styles/prism';
import { MdContentCopy, MdCheck } from 'react-icons/md';
import type { Components } from 'react-markdown';
import type { DetailedHTMLProps, HTMLAttributes } from 'react';
import {Details, Summary} from "@/app/components/chat-renderer/citation-component";

interface MarkdownRendererProps {
    content: string;
    className?: string;
    isFullHeight?: boolean;
    isStreaming?: boolean;
}

const CodeBlock: React.FC<{ language: string; value: string }> = ({ language, value }) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = async () => {
        await navigator.clipboard.writeText(value);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <div className="relative w-full">
            <div className="overflow-x-auto w-full">
                <button
                    onClick={handleCopy}
                    className="absolute right-2 top-2 p-2 rounded-lg hover:bg-gray-100 transition-colors z-10"
                    aria-label="Copy code"
                >
                    {copied ? (
                        <MdCheck className="w-5 h-5 text-green-500" />
                    ) : (
                        <MdContentCopy className="w-5 h-5 text-gray-500" />
                    )}
                </button>
                <SyntaxHighlighter
                    language={language}
                    style={oneLight as any}
                    customStyle={{
                        margin: '1rem 0',
                        padding: '1.5rem',
                        paddingRight: '3rem',
                        background: 'white',
                        borderRadius: '0.75rem',
                        maxWidth: '100%',
                        overflowX: 'auto',
                        wordBreak: 'break-word',
                        whiteSpace: 'pre-wrap'
                    }}
                    wrapLongLines={true}
                >
                    {value}
                </SyntaxHighlighter>
            </div>
        </div>
    );
};

const MemoizedIframe = React.memo<{ src?: string; title?: string; width?: string | number; height?: string | number; isFullHeight?: boolean; [key: string]: any }>(
    ({ src, title, width, height, isFullHeight, ...props }) => (
        <iframe
            src={src}
            title={title}
            className={isFullHeight ? "w-full h-full rounded-2xl" : "w-full h-screen rounded-2xl"}
            style={{
                border: 'none',
                background: 'transparent',
                outline: 'none',
                boxShadow: 'none',
                width: width || '100%',
                height: isFullHeight ? '100%' : (height || '80vh'),
                borderRadius: '1rem'
            }}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            {...props}
        />
    )
);

const createMarkdownComponents = (isFullHeight?: boolean): Components => ({
    details: Details,
    summary: Summary,
    iframe: (props) => <MemoizedIframe {...props} isFullHeight={isFullHeight} />,
    code(props) {
        const { className, children, ...rest } = props;
        const match = /language-(\w+)/.exec(className || '');
        const language = match ? match[1] : '';
        const value = String(children).replace(/\n$/, '');

        if (match) {
            return <CodeBlock language={language} value={value} />;
        }

        return (
            <code className="bg-gray-50 text-gray-800 rounded px-1.5 py-0.5 text-sm font-mono break-words max-w-full overflow-x-auto" {...rest}>
                {children}
            </code>
        );
    },
    p: ({ children }) => (
        <p className="my-1 md:my-3 leading-relaxed text-gray-700 break-words max-w-full">{children}</p>
    ),
    h1: ({ children }) => (
        <h1 className="text-2xl font-bold mt-6 mb-4 text-gray-900 break-words max-w-full">{children}</h1>
    ),
    h2: ({ children }) => (
        <h2 className="text-xl font-semibold mt-5 mb-3 text-gray-900 break-words max-w-full">{children}</h2>
    ),
    h3: ({ children }) => (
        <h3 className="text-lg font-medium mt-4 mb-2 text-gray-900 break-words max-w-full">{children}</h3>
    ),
    ul: ({ children }) => (
        <ul className="list-disc pl-6 my-4 space-y-2 text-gray-700 max-w-full overflow-x-auto">{children}</ul>
    ),
    ol: ({ children }) => (
        <ol className="list-decimal pl-6 my-4 space-y-2 text-gray-700 max-w-full overflow-x-auto">{children}</ol>
    ),
    li: ({ children }) => (
        <li className="leading-relaxed text-gray-700 break-words max-w-full">{children}</li>
    ),
    blockquote: ({ children }) => (
        <div className="px-4 bg-white max-w-full overflow-x-auto font-figtree">
            <blockquote className="border-l-4 border-newui-primary pl-4 italic text-newui-primary">
                {children}
            </blockquote>
        </div>
    ),
    strong: ({ children }) => (
        <strong className="font-bold text-gray-900 break-words">{children}</strong>
    ),
    a: ({ href, children }) => (
        <a
            href={href}
            className="text-blue-600 hover:text-blue-800 hover:underline break-words"
            target="_blank"
            rel="noopener noreferrer"
        >
            {children}
        </a>
    ),
    pre: ({ children, ...props }) => (
        <pre className="max-w-full overflow-x-auto" {...props}>
            {children}
        </pre>
    ),
    table: ({ children }) => (
        <div className="max-w-full overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
                {children}
            </table>
        </div>
    ),
});

const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({ content, className = '', isFullHeight = false, isStreaming = false }) => {
    const processContent = (text: string) => {
        const markdownBlockRegex = /```markdown\n([\s\S]*?)\n```/;
        const match = text.match(markdownBlockRegex);

        if (match) {
            const beforeText = text.slice(0, text.indexOf('```markdown')).trim();
            const afterText = text.slice(text.lastIndexOf('```') + 3).trim();

            return [beforeText, match[1], afterText]
                .filter(Boolean)
                .join('\n\n');
        }

        return text;
    };

    // Log content for debugging
    if (content.includes('<string>')) {
        console.error('⚠️ MarkdownRenderer received content with <string> tag:', content);
    }

    const components = React.useMemo(() => createMarkdownComponents(isFullHeight), [isFullHeight]);

    // Check if content is only an iframe
    const isOnlyIframe = content.trim().startsWith('<iframe') && content.trim().endsWith('</iframe>');

    if (isOnlyIframe && isFullHeight) {
        return (
            <div className="w-full h-full">
                <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    rehypePlugins={[rehypeRaw]}
                    components={components}
                >
                    {content}
                </ReactMarkdown>
            </div>
        );
    }

    return (
        <div className={`markdown-content text-white max-w-full overflow-hidden ${isFullHeight ? 'h-full' : ''} ${isStreaming ? 'markdown-streaming' : ''} p-0`}>
            <ReactMarkdown
                className={`prose prose-white max-w-none ${className}`}
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[rehypeRaw]}
                components={components}
            >
                {processContent(content)}
            </ReactMarkdown>
        </div>
    );
};

export default MarkdownRenderer;